<template>
  <AppModal
    :is-open="isOpen"
    title="Totals"
    :subtitle="`Enter what each player took.${isDouble ? ' Points are doubled.' : ''}`"
    @close="emit('close')"
  >
    <template #leading>
      <GameIndex symbol="Σ" />
    </template>

    <div class="steps segmented" role="tablist" aria-label="What to enter">
      <button
        v-for="step in steps"
        :key="step.key"
        type="button"
        role="tab"
        class="step-tab"
        :class="{ 'is-active': activeKey === step.key, 'is-done': step.done }"
        :aria-selected="activeKey === step.key"
        @click="activeKey = step.key"
      >
        <span class="step-name">{{ step.short }}</span>
        <span class="step-state">{{ step.state }}</span>
      </button>
    </div>

    <UnitPlayerList
      v-if="activeUnit"
      :key="activeUnit.key"
      :players="players"
      :allocation="activeUnit.allocation"
      :max="maxFor(activeUnit)"
      :unit-name="activeUnit.label"
      :points-for="previewFor"
      :is-open="isOpen"
    />

    <PlayerRadioList
      v-else
      class="king-picker"
      :players="players"
      :selected-id="kingOfHeartsPlayerId"
      label="Player who took the King of Hearts"
      :points-for="previewFor"
      @select="selectKing"
    />

    <template #footer>
      <p class="status" aria-live="polite">{{ statusText }}</p>
      <button v-if="isValid" type="button" class="btn btn-ink btn-block" @click="handleConfirm">
        Save round
      </button>
      <button
        v-else
        type="button"
        class="btn btn-ink btn-block"
        :disabled="!nextStep"
        @click="activeKey = nextStep.key"
      >
        {{ nextStep ? `Next: ${nextStep.label[0].toUpperCase()}${nextStep.label.slice(1)}` : 'Save round' }}
      </button>
    </template>
  </AppModal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useGameConfigStore } from '../stores/gameConfig';
import { GAME_TYPES } from '../constants/gameTypes';
import { useUnitAllocation } from '../composables/useUnitAllocation';
import AppModal from './AppModal.vue';
import GameIndex from './GameIndex.vue';
import PlayerRadioList from './PlayerRadioList.vue';
import UnitPlayerList from './UnitPlayerList.vue';

const props = defineProps({
  isOpen: Boolean,
  players: Array,
  isDouble: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['confirm', 'close']);

const gameConfig = useGameConfigStore();
const games = gameConfig.games;
const playerList = () => props.players ?? [];

const units = [
  {
    key: 'diamonds',
    label: 'diamonds',
    short: 'Diamonds',
    gameId: GAME_TYPES.DIAMONDS,
    allocation: useUnitAllocation(playerList, () =>
      games[GAME_TYPES.DIAMONDS].maxUnits(playerList().length)
    )
  },
  {
    key: 'queens',
    label: 'queens',
    short: 'Queens',
    gameId: GAME_TYPES.QUEENS,
    allocation: useUnitAllocation(playerList, () => games[GAME_TYPES.QUEENS].maxUnits())
  },
  {
    key: 'hands',
    label: 'hands',
    short: 'Hands',
    gameId: GAME_TYPES.LEVATE,
    allocation: useUnitAllocation(playerList, () => games[GAME_TYPES.LEVATE].maxUnits())
  }
];

const maxFor = (unit) => games[unit.gameId].maxUnits(playerList().length);

const activeKey = ref('diamonds');
const kingOfHeartsPlayerId = ref(null);

const activeUnit = computed(() => units.find((unit) => unit.key === activeKey.value) ?? null);

const reset = () => {
  units.forEach((unit) => unit.allocation.reset());
  kingOfHeartsPlayerId.value = null;
  activeKey.value = 'diamonds';
};

watch([() => props.isOpen, () => props.players], reset, { deep: true });

const selectKing = (playerId) => {
  kingOfHeartsPlayerId.value = kingOfHeartsPlayerId.value === playerId ? null : playerId;
};

const steps = computed(() => [
  ...units.map((unit) => {
    const remaining = unit.allocation.remaining.value;
    return {
      key: unit.key,
      label: unit.label,
      short: unit.short,
      done: remaining === 0,
      state: remaining === 0 ? '✓' : `${remaining} left`
    };
  }),
  {
    key: 'king',
    label: 'King of Hearts',
    short: 'King',
    done: kingOfHeartsPlayerId.value !== null,
    state: kingOfHeartsPlayerId.value !== null ? '✓' : 'Who?'
  }
]);

const isValid = computed(() => playerList().length > 0 && steps.value.every((step) => step.done));

const nextStep = computed(() => {
  const order = steps.value;
  const start = order.findIndex((step) => step.key === activeKey.value);
  const current = order[start];
  if (!current?.done) return null;
  return [...order.slice(start + 1), ...order.slice(0, start)].find((step) => !step.done) ?? null;
});

const statusText = computed(() => {
  if (isValid.value) return 'Everything is entered';
  const current = steps.value.find((step) => step.key === activeKey.value);
  if (current.key === 'king') {
    return current.done ? 'King of Hearts entered' : 'Tap the player who took the King of Hearts';
  }
  const unit = activeUnit.value;
  const remaining = unit.allocation.remaining.value;
  const max = maxFor(unit);
  return remaining === 0
    ? `All ${max} ${unit.label} assigned`
    : `${remaining} of ${max} ${unit.label} left to assign`;
});

const baseScore = (playerId) =>
  units.reduce(
    (sum, unit) => sum + (unit.allocation.counts.value[playerId] ?? 0) * games[unit.gameId].points,
    0
  ) + (kingOfHeartsPlayerId.value === playerId ? games[GAME_TYPES.KING_OF_HEARTS].points : 0);

const previewFor = (playerId) => baseScore(playerId) * (props.isDouble ? 2 : 1);

const handleConfirm = () => {
  if (!isValid.value) return;
  emit(
    'confirm',
    playerList().map((player) => ({ playerId: player.id, score: baseScore(player.id) }))
  );
};
</script>

<style scoped>
.steps {
  margin-bottom: 0.5rem;
}

.steps .step-tab {
  min-height: 52px;
  gap: 0.125rem;
  line-height: 1.1;
}

.step-name {
  font-family: var(--font-display);
  font-size: 1.0625rem;
}

.step-state {
  font-size: 0.75rem;
  font-weight: 500;
}

.king-picker {
  padding-top: 0.5rem;
}
</style>
